import { Component, Input, OnInit } from '@angular/core';
import { ChartService } from '@app/data/services/chartService';
import { BasicBarChartConfig, BasicChartDataSource } from '@app/data/interface/widget-config';
import { forkJoin } from 'rxjs';
import { take } from 'rxjs/operators';
import moment from 'moment-timezone';
import { EChartsOption, LineSeriesOption, SeriesOption } from 'echarts';
import { mapValueForChart, buildEchartsConfigFromDataSources } from '@app/widgets/helpers/widgets';
import { FilterService } from '@app/data/services/filterService';
import { SitesService } from '@app/data/services/sitesService';
import { BaseFilteredEnvironmentalDataChartComponent } from '../base-filtered-environmental-data-chart-component';
import {
  RawSeriesResponse,
  normalizeSeriesData,
  groupSeriesResponses,
  formatXAxisLabel,
} from '@app/widgets/helpers/time-series-aggregation.helper';

/**
 * I've really tried to use types in this file but the inheritence is making it difficult.
 */
@Component({
  selector: 'app-basic-bar-chart',
  templateUrl: './basic-bar-chart.component.html',
  styleUrls: ['./basic-bar-chart.component.scss'],
})
export class BasicBarChartComponent extends BaseFilteredEnvironmentalDataChartComponent implements OnInit {
  @Input() config!: BasicBarChartConfig;
  @Input() startDateOverride?: string;
  @Input() endDateOverride?: string;
  @Input() chartOptions: EChartsOption | null = null;

  ngOnInit() {
    super.ngOnInit();
    this.id = `basic-bar-chart-${this.config.dataConfig.widgetId}`;
    this.chartTitle = this.config.dataConfig.title || '';
    this.chartSubtitle = this.config.dataConfig.subtitle || '';
    this.domain = this.config.dataConfig.domain || '';

    // First load.
    this.setWidgetData();
  }

  /**
   * Clear any displayed chart data so the UI does not show stale results while
   * new data is being fetched. Implements the abstract contract from the base class.
   */
  protected clearChartData(): void {
    this.chartOptions = null;
    this.hasData = false;
  }

  /**
   * Populate responses for the chart
   *
   * If showBoundary is set in the config, center bars on ticks for bar charts, otherwise use no boundary gap
   *
   * Grouping by month will aggregate data points within each month according to the data source's valueField:
   * - 'sum' => sums values within the month
   * - 'avg' => averages values within the month
   *
   * Accepts a config to specify legend label priority.
   *
   * @param newChartOptions ECharts options object to populate
   * @param queryResponses Raw responses for each data source
   * @param xAxisData Optional array to populate with unique x-axis category values (date_time strings)
   * @param dateFormat Moment.js format string used to render x-axis labels (defaults to 'DD/MM/yyyy').
   */
  populateResponses(
    newChartOptions: EChartsOption,
    queryResponses: any,
    dataSources: BasicChartDataSource[],
    xAxisData: any[] = [],
    dateFormat: string = 'DD/MM/yyyy'
  ): void {
    // Sort out the axis.
    // We can't add it to the series yet because it might have missing data points
    // Add the x axis value
    newChartOptions = buildEchartsConfigFromDataSources(
      newChartOptions,
      dataSources as BasicChartDataSource[],
      this.allEnvironmentalDataSources,
      'bar',
      this.config?.dataConfig?.displayLabelOptions
    );

    queryResponses.forEach((seriesData: any, index: number) => {
      const dataLen = seriesData?.data?.length || 0;
      if (dataLen > 0) {
        this.hasData = true;
      }
      seriesData.data?.forEach((dataPoint: any) => {
        if (!xAxisData.includes(dataPoint.date_time)) {
          xAxisData.push(dataPoint.date_time);
        }
      });
    });

    if (!this.hasData) {
      this.chartOptions = {
        color: ['#6AB6B0'],
        title: {
          text: 'No data is available for the selected date range.',
        },
      };
    } else {
      // Set the data.
      xAxisData.sort((a: string, b: string) => (a > b ? 1 : -1));

      const shouldNormalize = Boolean(this.config?.dataConfig && this.config.dataConfig.normalize);
      const shouldHideZeroes = Boolean(this.config?.dataConfig && this.config.dataConfig.hideZeroes);
      const hideZeroesThreshold = (this.config?.dataConfig && this.config.dataConfig.hideZeroesThreshold) || null;

      if (shouldNormalize) {
        newChartOptions = buildEchartsConfigFromDataSources(
          newChartOptions,
          dataSources as BasicChartDataSource[],
          this.allEnvironmentalDataSources,
          'bar',
          this.config?.dataConfig?.displayLabelOptions
        );
        const normalized = normalizeSeriesData(queryResponses as RawSeriesResponse[], dataSources, xAxisData);
        normalized.forEach((seriesValues, index: number) => {
          const mapped = seriesValues.map((v) => mapValueForChart(v, shouldHideZeroes, hideZeroesThreshold));
          (newChartOptions.series as SeriesOption[])[index].data = mapped.map((value: any) => {
            if (value === null || value === undefined) {
              return value;
            }
            const stackId = (newChartOptions.series as SeriesOption[])[index]?.name ?? dataSources[index]?.label;
            if (typeof value === 'object' && !Array.isArray(value)) {
              return { ...value, stackId };
            }
            return { value, stackId };
          });
        });
      } else {
        // No normalization requested — align raw avg values from queryResponses to xAxisData order
        queryResponses.forEach((seriesData: any, index: number) => {
          const valueByDate: Record<string, any> = {};
          (seriesData.data || []).forEach((dp: any) => {
            valueByDate[dp.date_time] = dp.avg;
          });
          const mapped = xAxisData.map((date) =>
            mapValueForChart(valueByDate[date], shouldHideZeroes, hideZeroesThreshold)
          );
          (newChartOptions.series as SeriesOption[])[index].data = mapped.map((value: any) => {
            if (value === null || value === undefined) {
              return value;
            }
            const stackId = (newChartOptions.series as SeriesOption[])[index]?.name ?? dataSources[index]?.label;
            if (typeof value === 'object' && !Array.isArray(value)) {
              return { ...value, stackId };
            }
            return { value, stackId };
          });
        });
      }

      const existingXAxis: any = newChartOptions.xAxis || {};
      const existingAxisLabel = existingXAxis.axisLabel || {};

      const axisLabel = { ...existingAxisLabel };
      const groupBy = this.config?.dataConfig?.groupBy;
      if (groupBy) {
        axisLabel.formatter = (value: any) => formatXAxisLabel(value, groupBy);
      }

      const formatAsDate = this.config.dataConfig.formatXAxisAsDate !== false;

      newChartOptions.xAxis = {
        ...existingXAxis,
        type: 'category',
        boundaryGap: this.config.dataConfig.showBoundary,
        axisLabel,
        data: xAxisData.map((date: string) => (formatAsDate ? moment(date).format(dateFormat) : date)),
      };

      this.chartOptions = newChartOptions;
    }
  }

  /**
   * Set the data for the chart
   *
   * This method fetches data for each data source, processes it according to the configuration,
   * and populates the chart options for rendering.
   */
  setWidgetData() {
    let dateFormat = 'DD/MM/yyyy';
    const newChartOptions: EChartsOption = this.config.echartsConfig;
    const xAxisData: any[] = [];
    this.hasData = false;
    this.chartOptions = null;

    // If we don't have any data sources to look at, exit early.
    const preserveConfiguredLabels = Boolean(this.config?.dataConfig?.preserveConfiguredDataSourceLabels);
    const applicableDataSources = this.selectDataSources(this.config.dataConfig.dataSources, {
      preserveConfiguredLabels,
    });

    // If there's nothing to display....
    if (applicableDataSources.length === 0) {
      return;
    }

    // Get the data
    forkJoin(
      applicableDataSources.map((source: any) =>
        this.chartService
          .getEnvironmentalDataForSourceAndAnalyteByDate(
            source.environmentalDataSourceIdentifier,
            source.analyteIdentifier,
            moment(this.startDate).format('YYYY-MM-DD'),
            moment(this.endDate).format('YYYY-MM-DD'),
            this.startDate === this.endDate ? 'hour' : 'day'
          )
          .pipe(take(1))
      )
    ).subscribe({
      next: (queryResponses: any) => {
        let responsesToUse = queryResponses;
        const groupBy = this.config?.dataConfig?.groupBy;
        if (groupBy) {
          responsesToUse = groupSeriesResponses(
            queryResponses,
            applicableDataSources as BasicChartDataSource[],
            groupBy
          );
          if (groupBy.grouping_type === 'time') {
            dateFormat = groupBy.period === 'month' ? 'MMM YYYY' : '[Q]Q YYYY';
          }
        }

        this.populateResponses(
          newChartOptions,
          responsesToUse,
          applicableDataSources as BasicChartDataSource[],
          xAxisData,
          dateFormat
        );
      },
      error: (err: any) => {
        console.error(err);
        this.chartOptions = {
          color: ['#6AB6B0'],
          title: {
            text: 'No data is available.',
          },
        };
      },
    });
  }
}
